import {
    makeStyles,
    MessageBar,
    MessageBarBody,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    tokens,
} from '@fluentui/react-components';
import React, { useEffect, useMemo, useState } from 'react';
import type { ArbeitszeitItem } from '../../models/ArbeitszeitItem';
import type { PlanwertSollstundenProjektmitarbeiterItem } from '../../models/PlanwertSollstundenProjektmitarbeiterItem';
import type { DataService } from '../../services/dataService';

type ProjectTimeRow = { personalId: number; name: string; planned: number; hoursByPackage: Record<string, number> };

const formatHoursHM = (hours: number) => {
    const totalMinutes = Math.round(hours * 60);
    return `${Math.floor(totalMinutes / 60)}:${String(totalMinutes % 60).padStart(2, '0')}`;
};

const headerRowStyle: React.CSSProperties = {
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
};

const headerCellStyle: React.CSSProperties = { fontWeight: 700 };

const buildProjectTimeTable = (arbeitszeiten: ArbeitszeitItem[], planwerte: PlanwertSollstundenProjektmitarbeiterItem[]) => {
    const packages = new Set<string>();
    const rows = new Map<number, ProjectTimeRow>();
    const getRow = (personalId: number, name: string) => {
        let row = rows.get(personalId);
        if (!row) {
            row = { personalId, name, planned: 0, hoursByPackage: {} };
            rows.set(personalId, row);
        }
        return row;
    };

    planwerte.forEach((item) => {
        getRow(item.personalID, item.fullName || `${item.vorname} ${item.nachname}`).planned += item.sollstunden || 0;
    });

    arbeitszeiten.forEach((item) => {
        const von = new Date(item.von);
        const bis = new Date(item.bis);
        if (Number.isNaN(von.getTime()) || Number.isNaN(bis.getTime())) return;
        const packageName = item.arbeitspaketBezeichnung?.trim() || '-';
        packages.add(packageName);
        const row = getRow(item.personalID, item.fullName || `${item.vorname} ${item.nachname}`);
        row.hoursByPackage[packageName] = (row.hoursByPackage[packageName] || 0) + (bis.getTime() - von.getTime()) / 3600000;
    });

    return {
        packages: [...packages].sort((a, b) => a.localeCompare(b)),
        rows: [...rows.values()].sort((a, b) => a.name.localeCompare(b.name)),
    };
};

const useStyles = makeStyles({
    tableWrapper: {
        width: 'fit-content',
        minWidth: '50%',
        maxWidth: '100%',
        overflowX: 'auto',
    },
    placeholder: {
        color: tokens.colorNeutralForeground3,
        fontStyle: 'italic',
        fontSize: tokens.fontSizeBase300,
    },
});

type EckdatenOverviewProjectTimeProps = {
    dataService: DataService | null;
    projektId: number;
};

export const EckdatenOverviewProjectTime: React.FC<EckdatenOverviewProjectTimeProps> = ({ dataService, projektId }) => {
    const styles = useStyles();
    const [arbeitszeiten, setArbeitszeiten] = useState<ArbeitszeitItem[]>([]);
    const [planwerte, setPlanwerte] = useState<PlanwertSollstundenProjektmitarbeiterItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const projectTimeTable = useMemo(() => buildProjectTimeTable(arbeitszeiten, planwerte), [arbeitszeiten, planwerte]);

    useEffect(() => {
        if (!dataService || !projektId) return;
        let cancelled = false;
        Promise.all([dataService.getArbeitszeiten(projektId), dataService.getPlanwerteSollstundenProjektmitarbeiterListe(projektId)])
            .then(([zeiten, plan]) => {
                if (!cancelled) {
                    setArbeitszeiten(zeiten);
                    setPlanwerte(plan);
                    setError(null);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load project time');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [dataService, projektId]);

    if (loading) {
        return <Spinner size="tiny" label="Loading project time..." />;
    }
    if (error) {
        return (
            <MessageBar intent="error">
                <MessageBarBody>{error}</MessageBarBody>
            </MessageBar>
        );
    }

    const { packages, rows } = projectTimeTable;
    if (packages.length === 0) {
        return <div className={styles.placeholder}>No work times recorded.</div>;
    }

    return (
        <div className={styles.tableWrapper}>
            <Table size="small" aria-label="Project time">
                <TableHeader>
                    <TableRow style={headerRowStyle}>
                        <TableHeaderCell style={headerCellStyle}></TableHeaderCell>
                        <TableHeaderCell style={headerCellStyle}>Planned hours</TableHeaderCell>
                        <TableHeaderCell colSpan={packages.length} style={headerCellStyle}>Hours timetracking</TableHeaderCell>
                    </TableRow>
                    <TableRow style={headerRowStyle}>
                        <TableHeaderCell style={headerCellStyle}>Mitarbeiter</TableHeaderCell>
                        <TableHeaderCell style={headerCellStyle}>Calculation</TableHeaderCell>
                        {packages.map((packageName) => (
                            <TableHeaderCell key={packageName} style={headerCellStyle}>{packageName}</TableHeaderCell>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.personalId}>
                            <TableCell>{row.name}</TableCell>
                            <TableCell>{row.planned ? formatHoursHM(row.planned) : '-'}</TableCell>
                            {packages.map((packageName) => (
                                <TableCell key={packageName}>
                                    {row.hoursByPackage[packageName] ? formatHoursHM(row.hoursByPackage[packageName]) : '-'}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};
